import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../../ui/TouchTarget.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrictProgress,
  getNumberKingdomState,
  saveNumberKingdomDistrictProgress,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import { createNumberKingdomHomeButton, type NumberKingdomHomeButton } from '../../components/NumberKingdomHomeButton.js';
import {
  SUBTRACTION_KEEP_ASSETS,
  SUBTRACTION_KEEP_ASSET_LIST,
  canEnterSubtractionKeep,
  getMissingSubtractionKeepAssets,
} from './SubtractionKeepAssets.js';
import {
  SUBTRACTION_KEEP_CHALLENGES,
  type SubtractionKeepChallengeConfig,
} from './SubtractionKeepConfig.js';
import {
  SubtractionKeepState,
  type SubtractionKeepSerializedState,
  type SubtractionKeepSnapshot,
} from './SubtractionKeepState.js';
import { canEnterMeasurementMarket } from '../measurement-market/MeasurementMarketAssets.js';

export const SUBTRACTION_KEEP_SCENE_KEY = 'SubtractionKeep';
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface SubtractionKeepProgress {
  readonly version: 1;
  readonly challengeIndex: number;
  readonly challengeId: string;
  readonly challengeState: SubtractionKeepSerializedState;
  readonly completedChallengeIds: readonly string[];
  readonly restored: boolean;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly action: () => void;
}

export class SubtractionKeepScene extends Phaser.Scene {
  static readonly requiredImageKeys = SUBTRACTION_KEEP_ASSET_LIST.map(asset => asset.key);

  private challengeIndex = 0;
  private completedChallengeIds = new Set<string>();
  private challenge!: SubtractionKeepState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private exchangeVault!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private equationText!: Phaser.GameObjects.Text;
  private placeValueText!: Phaser.GameObjects.Text;
  private removedText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private nextText!: Phaser.GameObjects.Text;
  private buttons: ArtButton[] = [];
  private pieces: Phaser.GameObjects.Image[] = [];
  private restored = false;
  private suspended = false;
  private leaving = false;
  private reducedMotion = false;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: SUBTRACTION_KEEP_SCENE_KEY }); }

  create() {
    this.buttons = [];
    this.pieces = [];
    this.challengeIndex = 0;
    this.completedChallengeIds = new Set<string>();
    this.leaving = false;
    this.suspended = false;

    const world = getNumberKingdomState();
    const missing = getMissingSubtractionKeepAssets(this.textures);
    if (!world.regroupingWorkshopPowered || !canEnterSubtractionKeep(this.textures)) {
      if (missing.length > 0) {
        console.error('[SubtractionKeep] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('RegroupingWorkshop');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.leaving = false;
    this.suspended = false;
    this.restored = world.districtCompletion[NUMBER_KINGDOM_DISTRICTS.subtractionCaverns] === true;
    this.restoreProgress(world.districtProgress[NUMBER_KINGDOM_DISTRICTS.subtractionCaverns]);
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.subtractionCaverns);
    SaveManager.setLastScene(SUBTRACTION_KEEP_SCENE_KEY);
    SaveManager.save();

    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.cameras.main.fadeIn(this.reducedMotion ? 140 : 420, 255, 220, 150);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<SubtractionKeepProgress> | undefined;
    if (progress?.version === 1) {
      this.challengeIndex = Phaser.Math.Clamp(
        Number.isInteger(progress.challengeIndex) ? Number(progress.challengeIndex) : 0,
        0,
        SUBTRACTION_KEEP_CHALLENGES.length - 1,
      );
      this.completedChallengeIds = new Set(
        (progress.completedChallengeIds ?? []).filter(id =>
          SUBTRACTION_KEEP_CHALLENGES.some(config => config.id === id)),
      );
      this.restored ||= progress.restored === true;
      const config = SUBTRACTION_KEEP_CHALLENGES[this.challengeIndex];
      const saved = progress.challengeId === config.id ? progress.challengeState : undefined;
      this.challenge = new SubtractionKeepState(config, saved);
      return;
    }
    this.challenge = new SubtractionKeepState(SUBTRACTION_KEEP_CHALLENGES[0]);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, SUBTRACTION_KEEP_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, SUBTRACTION_KEEP_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1).setAlpha(this.restored ? 1 : 0);
    this.exchangeVault = this.add.image(640, 390, SUBTRACTION_KEEP_ASSETS.exchangeVault.key)
      .setDepth(24).setInteractive({ useHandCursor: true });
    this.exchangeVault.on('pointerup', () => this.exchangeTen());
    this.missionPanel = this.add.image(640, 105, SUBTRACTION_KEEP_ASSETS.missionPanel.key).setDepth(30);
    this.cosmo = this.add.image(110, 640, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key).setDepth(90);
  }

  private buildInterface() {
    this.titleText = this.makeText('SUBTRACTION KEEP', 29, '#fff2ad', 'bold')
      .setStroke('#2c1746', 7).setDepth(105);
    this.missionText = this.makeText('', 17, '#ffffff', 'bold')
      .setStroke('#233a69', 5).setWordWrapWidth(650).setDepth(105);
    this.equationText = this.makeText('', 32, '#fff2ad', 'bold')
      .setStroke('#26375c', 7).setDepth(105);
    this.placeValueText = this.makeText('', 17, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setDepth(105);
    this.removedText = this.makeText('', 15, '#dffcff', 'bold')
      .setStroke('#26375c', 5).setDepth(105);
    this.feedbackText = this.makeText('', 16, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setWordWrapWidth(720).setDepth(105);
    this.controlsText = this.makeText(
      'Tap pieces to remove  •  X exchange ten  •  T ten  •  O one  •  Enter check/next  •  H hint  •  R reset',
      12,
      '#fff8db',
      'bold',
    ).setStroke('#26375c', 5).setDepth(105);
    this.backText = this.makeText('←  REGROUPING WORKSHOP', 15, '#ffffff', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(106);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);
    this.nextText = this.makeText('MEASUREMENT MARKET  →  (K)', 15, '#fff2ad', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(106);
    [this.backText, this.nextText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToWorkshop, this);
    this.nextText.on('pointerup', this.travelToMeasurementMarket, this);

    this.buttons = [
      this.makeButton('CHECK', () => this.primaryAction()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.resetChallenge()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, SUBTRACTION_KEEP_ASSETS.actionButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 14, '#ffffff', 'bold').setStroke('#26375c', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text, action };
  }

  private exchangeTen() {
    if (!this.canAct()) return;
    const result = this.challenge.decomposeTen();
    if (result === 'accepted') {
      this.feedbackText.setText('One ten opens into ten ones. The value stays the same!');
      this.playMagicBurst(this.exchangeVault.x, this.exchangeVault.y, 260);
      audio.correct();
    } else if (result === 'already-decomposed') {
      this.feedbackText.setText('You already exchanged one ten. Now remove the needed pieces.');
      audio.wrong();
    }
    this.capture();
  }

  private removeOne() {
    if (!this.canAct()) return;
    const result = this.challenge.removeOne();
    if (result === 'needs-decomposition') {
      this.feedbackText.setText('No ones remain. Exchange one ten so there are enough ones to remove.');
      audio.wrong();
    } else if (result === 'nothing-to-remove') {
      this.feedbackText.setText('You have removed all the ones the subtraction asks for.');
      audio.wrong();
    } else audio.click();
    this.capture();
  }

  private removeTen() {
    if (!this.canAct()) return;
    const result = this.challenge.removeTen();
    if (result === 'nothing-to-remove') {
      this.feedbackText.setText('You have removed all the tens the subtraction asks for.');
      audio.wrong();
    } else audio.click();
    this.capture();
  }

  private primaryAction() {
    if (this.restored) return;
    if (this.challenge.snapshot.complete) {
      this.startNextChallenge();
      return;
    }
    const result = this.challenge.check();
    if (result !== 'complete') {
      const snapshot = this.challenge.snapshot;
      this.feedbackText.setText(
        `Keep working: remove ${snapshot.requiredTensToRemove - snapshot.removedTens} more tens and ${snapshot.requiredOnesToRemove - snapshot.removedOnes} more ones.`,
      );
      audio.wrong();
      this.capture();
      return;
    }
    const config = this.currentConfig;
    this.completedChallengeIds.add(config.id);
    SaveManager.recordAnswer(config.skillId, true);
    this.feedbackText.setText(`${config.minuend} − ${config.subtrahend} = ${config.difference}. The keep responds!`);
    audio.correct();
    if (this.completedChallengeIds.size === SUBTRACTION_KEEP_CHALLENGES.length) {
      this.completeDistrict();
    } else {
      this.buttons[0].label.setText('NEXT CHALLENGE');
      this.playMagicBurst(640, 360, 330);
      this.capture();
    }
    this.refresh();
  }

  private startNextChallenge() {
    if (!this.challenge.snapshot.complete || this.restored) return;
    const nextIndex = SUBTRACTION_KEEP_CHALLENGES.findIndex(
      config => !this.completedChallengeIds.has(config.id),
    );
    if (nextIndex < 0) return this.completeDistrict();
    this.challengeIndex = nextIndex;
    this.challenge = new SubtractionKeepState(this.currentConfig);
    this.feedbackText.setText('A new lock slides into place. Build the minuend, exchange, and subtract.');
    this.buttons[0].label.setText('CHECK');
    this.capture();
  }

  private completeDistrict() {
    this.restored = true;
    this.completedChallengeIds = new Set(SUBTRACTION_KEEP_CHALLENGES.map(config => config.id));
    completeNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
      this.progressSnapshot(),
      NUMBER_KINGDOM_DISTRICTS.measureworks,
    );
    this.restoredBackground.setAlpha(1);
    this.dormantBackground.setAlpha(0);
    this.feedbackText.setText('Every lock is solved. The gate opens and the keep fills with number light!');
    this.playMagicBurst(640, 355, 560);
    audio.restoration();
    this.refresh();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.challenge.requestHint() ?? 'Look closely at the tens and ones.');
    audio.click();
    this.capture();
  }

  private resetChallenge() {
    if (!this.canAct()) return;
    this.challenge = new SubtractionKeepState(this.currentConfig);
    this.feedbackText.setText('This lock is reset. Your completed locks are still safe.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
      this.progressSnapshot(),
    );
    this.refresh();
  }

  private progressSnapshot(): SubtractionKeepProgress {
    return {
      version: 1,
      challengeIndex: this.challengeIndex,
      challengeId: this.currentConfig.id,
      challengeState: this.challenge.serialize(),
      completedChallengeIds: [...this.completedChallengeIds],
      restored: this.restored,
    };
  }

  private refresh() {
    const snapshot = this.challenge.snapshot;
    const config = this.currentConfig;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.exchangeVault.setVisible(!this.restored);
    this.missionText.setText(this.restored
      ? 'The Subtraction Keep gate is restored. Replay any lock whenever you want.'
      : `Lock ${this.challengeIndex + 1} of ${SUBTRACTION_KEEP_CHALLENGES.length}: remove ${config.subtrahend} from ${config.minuend}. Exchange one ten when the ones are not enough.`);
    this.equationText.setText(this.restored
      ? 'KEEP RESTORED'
      : `${config.minuend} − ${config.subtrahend} = ?`);
    this.placeValueText.setText(
      `REMAINING  ${snapshot.remainingTens} tens  •  ${snapshot.remainingOnes} ones  •  value ${snapshot.currentValue}`,
    );
    this.removedText.setText(
      `REMOVED  ${snapshot.removedTens}/${snapshot.requiredTensToRemove} tens  •  ${snapshot.removedOnes}/${snapshot.requiredOnesToRemove} ones`,
    );
    this.buttons.forEach(button => {
      button.image.setVisible(!this.restored);
      button.label.setVisible(!this.restored);
    });
    this.controlsText.setVisible(!this.restored);
    this.nextText.setVisible(this.restored && canEnterMeasurementMarket(this.textures));
    if (!this.restored) {
      this.buttons[0].label.setText(snapshot.complete ? 'NEXT CHALLENGE' : 'CHECK');
    }
    if (!this.feedbackText.text) {
      this.feedbackText.setText('The keep remembers every piece you move, even after a resize or reload.');
    }
    this.renderPieces(snapshot);
    this.layout();
  }

  private renderPieces(snapshot: SubtractionKeepSnapshot) {
    this.pieces.forEach(piece => piece.destroy());
    this.pieces = [];
    if (this.restored) return;
    for (let index = 0; index < snapshot.remainingTens; index += 1) {
      const piece = this.add.image(0, 0, NUMBER_KINGDOM_ASSETS.tenRod.key)
        .setDepth(55).setInteractive({ useHandCursor: true }).setData('piece', { kind: 'ten', index });
      piece.on('pointerup', () => this.removeTen());
      this.pieces.push(piece);
    }
    for (let index = 0; index < snapshot.remainingOnes; index += 1) {
      const piece = this.add.image(0, 0, NUMBER_KINGDOM_ASSETS.oneCube.key)
        .setDepth(56).setInteractive({ useHandCursor: true }).setData('piece', { kind: 'one', index });
      piece.on('pointerup', () => this.removeOne());
      this.pieces.push(piece);
    }
  }

  private playMagicBurst(x: number, y: number, size: number) {
    const burst = this.add.image(x, y, SUBTRACTION_KEEP_ASSETS.magicBurst.key)
      .setDisplaySize(size, size).setDepth(80).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => burst.destroy());
    else this.tweens.add({
      targets: burst,
      alpha: { from: 0, to: 0.9 },
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 520,
      yoyo: true,
      hold: 140,
      onComplete: () => burst.destroy(),
    });
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToWorkshop();
    if (key === 'm') return this.returnHome();
    if (key === 'k' && this.restored) return this.travelToMeasurementMarket();
    if (key === 'x') return this.exchangeTen();
    if (key === 't') return this.removeTen();
    if (key === 'o') return this.removeOne();
    if (key === 'enter' || key === ' ') return this.primaryAction();
    if (key === 'h') return this.hint();
    if (key === 'r') this.resetChallenge();
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    const portrait = safe.width < 700;
    const panelWidth = portrait
      ? Math.min(500, safe.width - 30)
      : Math.min(compact ? 630 : 720, safe.width - 230);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 132 : compact ? 86 : 94))
      .setDisplaySize(panelWidth, panelWidth * (360 / 900));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 61 : 26))
      .setFontSize(portrait ? 23 : compact ? 22 : 29);
    this.missionText.setPosition(safe.centerX, safe.y + (portrait ? 137 : compact ? 91 : 100))
      .setFontSize(portrait ? 13 : compact ? 14 : 17).setWordWrapWidth(panelWidth * 0.8);
    this.backText.setPosition(safe.x + (portrait ? 238 : compact ? 250 : 282), safe.y + 30)
      .setFontSize(portrait ? 10 : compact ? 12 : 15);
    this.homeButton.layout(safe, compact || portrait, 'left');
    this.nextText.setPosition(safe.centerX, safe.bottom - 38)
      .setFontSize(portrait ? 11 : compact ? 12 : 15);
    this.equationText.setPosition(safe.centerX, safe.y + (portrait ? 232 : compact ? 185 : 205))
      .setFontSize(portrait ? 27 : compact ? 27 : 32);
    this.placeValueText.setPosition(safe.centerX, safe.y + (portrait ? 276 : compact ? 228 : 250))
      .setFontSize(portrait ? 13 : compact ? 14 : 17);
    this.removedText.setPosition(safe.centerX, safe.y + (portrait ? 306 : compact ? 256 : 282))
      .setFontSize(portrait ? 12 : compact ? 13 : 15);
    this.exchangeVault.setPosition(safe.centerX, safe.y + (portrait ? 445 : compact ? 405 : 425))
      .setDisplaySize(portrait ? 175 : compact ? 210 : 245, portrait ? 175 : compact ? 210 : 245);
    const buttonY = safe.bottom - (compact ? 70 : 78);
    const gap = portrait ? Math.min(150, (safe.width - 120) / 2) : compact ? 145 : 165;
    this.buttons.forEach((button, index) => {
      const x = safe.centerX + (index - 1) * gap;
      button.image.setPosition(x, buttonY)
        .setDisplaySize(portrait ? 112 : compact ? 128 : 145, portrait ? 56 : compact ? 64 : 72);
      button.label.setPosition(x, buttonY).setFontSize(portrait ? 10 : compact ? 12 : 14);
    });
    this.feedbackText.setPosition(safe.centerX, safe.bottom - (compact ? 122 : 136))
      .setFontSize(portrait ? 12 : compact ? 13 : 16)
      .setWordWrapWidth(portrait ? safe.width - 50 : Math.min(720, safe.width - 250));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 20)
      .setFontSize(portrait ? 8 : compact ? 10 : 12)
      .setWordWrapWidth(portrait ? safe.width - 30 : safe.width);
    this.cosmo.setPosition(safe.x + (compact ? 58 : 74), safe.bottom - (compact ? 50 : 60))
      .setDisplaySize(compact ? 88 : 108, compact ? 79 : 96);
    this.positionPieces(safe.centerX, safe.y, compact, portrait);
  }

  private positionPieces(centerX: number, top: number, compact: boolean, portrait: boolean) {
    const tens = this.pieces.filter(piece => piece.getData('piece').kind === 'ten');
    const ones = this.pieces.filter(piece => piece.getData('piece').kind === 'one');
    const tenWidth = portrait ? 34 : compact ? 44 : 50;
    const tenHeight = portrait ? 76 : compact ? 96 : 108;
    tens.forEach((piece, index) => piece
      .setPosition(
        centerX - (portrait ? 205 : compact ? 300 : 335) + index * (tenWidth + 4),
        top + (portrait ? 440 : compact ? 400 : 420),
      )
      .setDisplaySize(tenWidth, tenHeight));
    const oneSize = portrait ? 34 : compact ? 43 : 48;
    ones.forEach((piece, index) => {
      const column = index % 5;
      const row = Math.floor(index / 5);
      piece.setPosition(
        centerX + (portrait ? 72 : compact ? 190 : 215) + column * (oneSize + 3),
        top + (portrait ? 410 : compact ? 370 : 390) + row * (oneSize + 3),
      ).setDisplaySize(oneSize, oneSize);
    });
  }

  private get currentConfig(): SubtractionKeepChallengeConfig {
    return SUBTRACTION_KEEP_CHALLENGES[this.challengeIndex];
  }

  private canAct() {
    return !this.leaving && !this.suspended && !this.restored && !this.challenge.snapshot.complete;
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private readonly returnToWorkshop = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
      this.progressSnapshot(),
    );
    this.scene.start('RegroupingWorkshop');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
      this.progressSnapshot(),
    );
    SaveManager.setLastScene(SUBTRACTION_KEEP_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly travelToMeasurementMarket = () => {
    if (!this.restored || this.leaving || !canEnterMeasurementMarket(this.textures)) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
      this.progressSnapshot(),
    );
    this.scene.start('MeasurementMarket');
  };

  private shutdown() {
    if (this.challenge) {
      saveNumberKingdomDistrictProgress(
        NUMBER_KINGDOM_DISTRICTS.subtractionCaverns,
        this.progressSnapshot(),
      );
    }
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.pieces.forEach(piece => piece.destroy());
    this.pieces = [];
    this.tweens.killAll();
  }
}
